/**
 * AllowedModelsDiffView — friendly label view for a `handler_config`
 * proposal that touches `allowed_models`.
 *
 * The AoA fires `propose_edit_agent(field='handler_config', new_value='{
 *   "allowed_models": ["gpt-mini", "haiku"] }')`
 * and the regular ProposalCard would render that as raw JSON. That's
 * hostile UX for a list — the user has to mentally parse model ids.
 * This view instead shows two columns of model CHIPS labeled with
 * each model's display name + provider, the same way the Edit-Agent
 * page renders its model picker. The Edit button is suppressed for
 * model proposals (see ProposalCard.isModelProposal), so the user
 * can only Accept or Reject what's shown here — meaning the labels
 * must be unambiguous.
 *
 * MODEL FAMILIES: `/ai/models/` returns FAMILY entries ('sonnet',
 * 'gpt-mini', ...) with the pinned concrete version as read-only
 * `resolved_model` metadata. Proposals now carry family keys, but the
 * agent's stored config (proposal.old_value) may still hold legacy
 * concrete ids. BOTH sides are normalized to family keys before
 * diffing so a legacy→family spelling change never renders as an
 * add/remove. Refs that match no family (retired versions the backend
 * still resolves at run time) are rendered as-is.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import api from '@/services/api';
import './AllowedModelsDiffView.css';

interface ModelInfo {
  /** Family key ('sonnet', 'gpt-mini', ...) — the stable, selectable id. */
  id: string;
  name: string;
  provider: string;
  /** Concrete version the family is pinned to — read-only display. */
  resolved_model?: string;
  /** Every release in this family — lets a legacy ref still match. */
  version_ids?: string[];
  resolved_model_name?: string;
  aliases?: string[];
}

type AllowedValue = 'all' | string[];

interface Props {
  /** Current value (from proposal.old_value). */
  before: AllowedValue;
  /** Proposed new value (from proposal.new_value). */
  after: AllowedValue;
}

/** Normalize the various shapes `allowed_models` can land in. */
function normalizeAllowed(value: unknown): AllowedValue {
  if (value === null || value === undefined) return 'all';
  if (value === 'all') return 'all';
  if (Array.isArray(value)) {
    const ids = value.filter((v): v is string => typeof v === 'string' && v.length > 0);
    return ids.length > 0 ? ids : 'all';
  }
  if (typeof value === 'string') {
    if (value === 'all' || value === '') return 'all';
    // Legacy comma-separated string.
    const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
    return parts.length > 0 ? parts : 'all';
  }
  return 'all';
}

export const AllowedModelsDiffView: React.FC<Props> = ({ before, after }) => {
  const { t } = useLanguage();
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Pull the platform's model registry once. The same endpoint
  // ChatInput uses. Already tenant-filtered server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/ai/models/');
        const data = response.data;
        const list: ModelInfo[] = (data.models || data || []).map((m: any) => ({
          id: m.id,
          name: m.name || m.id,
          provider: m.provider || '',
          resolved_model: m.resolved_model || undefined,
          version_ids: m.version_ids || undefined,
          resolved_model_name: m.resolved_model_name || undefined,
          aliases: m.aliases || undefined,
        }));
        if (!cancelled) setModels(list);
      } catch (e) {
        if (!cancelled) setLoadError(t('metaAgent.couldNotLoadModelLabels'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lookup keyed by BOTH the family key and its pinned concrete version,
  // so a stored legacy ref ('claude-haiku-4-5-...') and its family key
  // ('haiku') land on the same entry.
  const byRef = useMemo(() => {
    const m = new Map<string, ModelInfo>();
    for (const x of models) {
      m.set(x.id, x);
      if (x.resolved_model) m.set(x.resolved_model, x);
    }
    // A switched-off family's key names the family that runs it ("gpt" on
    // GPT Terra while GPT is off).
    for (const x of models) for (const a of x.aliases || []) if (!m.has(a)) m.set(a, x);
    return m;
  }, [models]);

  /** Map every ref to its family key (de-duplicated, order-preserving).
   *  Refs matching no family stay as-is — the backend still resolves
   *  retired concrete versions, so we render them rather than crash. */
  const toFamilyKeys = useMemo(
    () => (value: AllowedValue): AllowedValue => {
      if (value === 'all') return 'all';
      const seen = new Set<string>();
      const out: string[] = [];
      for (const ref of value) {
        const key = byRef.get(ref)?.id ?? ref;
        if (!seen.has(key)) {
          seen.add(key);
          out.push(key);
        }
      }
      return out;
    },
    [byRef],
  );

  const beforeNorm = useMemo(
    () => toFamilyKeys(normalizeAllowed(before)),
    [before, toFamilyKeys],
  );
  const afterNorm = useMemo(
    () => toFamilyKeys(normalizeAllowed(after)),
    [after, toFamilyKeys],
  );

  // What changed: ids only in `after` (added), ids only in `before` (removed).
  const { added, removed, kept } = useMemo(() => {
    if (afterNorm === 'all' && beforeNorm === 'all') {
      return { added: [], removed: [], kept: [] as string[] };
    }
    if (afterNorm === 'all') {
      // Going from a specific list to "all" — everything is newly allowed.
      return { added: [], removed: [], kept: [] as string[] };
    }
    if (beforeNorm === 'all') {
      // Going from "all" to a specific list — every id in `after` is "kept-but-now-restricted".
      return { added: [], removed: [], kept: afterNorm };
    }
    const beforeSet = new Set(beforeNorm);
    const afterSet = new Set(afterNorm);
    return {
      added: afterNorm.filter((id) => !beforeSet.has(id)),
      removed: beforeNorm.filter((id) => !afterSet.has(id)),
      kept: afterNorm.filter((id) => beforeSet.has(id)),
    };
  }, [beforeNorm, afterNorm]);

  const renderChip = (id: string, variant: 'kept' | 'added' | 'removed') => {
    const meta = byRef.get(id);
    const label = meta?.name || id;
    const sub = meta?.provider || '';
    // Pinned concrete version — read-only context, never selectable.
    const resolved = meta?.resolved_model_name || '';
    return (
      <span
        key={`${variant}-${id}`}
        className={`aoa-allowed-chip aoa-allowed-chip--${variant}`}
        title={resolved ? `${label} — ${resolved}` : id}
      >
        <span className="aoa-allowed-chip__label">{label}</span>
        {sub && <span className="aoa-allowed-chip__provider">{sub}</span>}
        {resolved && <span className="aoa-allowed-chip__resolved">{resolved}</span>}
      </span>
    );
  };

  return (
    <div className="aoa-allowed-models">
      <div className="aoa-allowed-models__section">
        <div className="aoa-allowed-models__heading">
          {t('metaAgent.modelsTheUserWillBe')}
        </div>
        {afterNorm === 'all' ? (
          <div className="aoa-allowed-models__chips">
            <span className="aoa-allowed-chip aoa-allowed-chip--all">
              <span className="aoa-allowed-chip__label">{t('metaAgent.allPlatformModels')}</span>
              <span className="aoa-allowed-chip__provider">{t('metaAgent.noPerAgentRestriction')}</span>
            </span>
          </div>
        ) : (
          <div className="aoa-allowed-models__chips">
            {kept.map((id) => renderChip(id, 'kept'))}
            {added.map((id) => renderChip(id, 'added'))}
          </div>
        )}
      </div>

      {removed.length > 0 && (
        <div className="aoa-allowed-models__section">
          <div className="aoa-allowed-models__heading aoa-allowed-models__heading--removed">
            {t('metaAgent.willBeRemovedFromThe')}
          </div>
          <div className="aoa-allowed-models__chips">
            {removed.map((id) => renderChip(id, 'removed'))}
          </div>
        </div>
      )}

      {beforeNorm !== 'all' && afterNorm === 'all' && (
        <div className="aoa-allowed-models__hint">
          {t('metaAgent.allowedModels.wasRestrictedTo', { count: Array.isArray(beforeNorm) ? beforeNorm.length : 0 })}
        </div>
      )}

      {loadError && (
        <div className="aoa-allowed-models__hint aoa-allowed-models__hint--warn">
          {t('metaAgent.allowedModels.showingRawIds', { error: loadError })}
        </div>
      )}
    </div>
  );
};
